import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useFormikContext, getIn } from 'formik';
import { Grid } from '@material-ui/core';
import {
  FarmRiskType,
  FarmScheduleCoverageType,
  FarmScheduleCoverageTypesByState,
  lookupsJson
} from '@ourbranch/lookups';

import { appConfig } from 'brand-config';
import { BrandFormField } from 'common/components/farm/brand-form-field';
import { useActiveFormState } from 'common/hooks/use-active-form-state';
import CollapsibleCard from 'core/components/collapsible-card';
import Field from 'core/components/form/form.v2';
import Label from 'core/components/label/label';
import { FarmIncomeCoverageForm, InlandMarineForm } from '../../sub-risks';

import useStyles from './FO6-unscheduled.styles';

const eqDeductibleOptions = lookupsJson.farm.earthquakeDeductible.filter((d) => d.id !== 0.02);

const allOtherDeductibleOptions = lookupsJson.farm.deductibleAllOther;
const defaultBlanketDeductibleOptions = allOtherDeductibleOptions.filter((d) => d.id >= 1500);

const riskType = FarmRiskType.Fo6Unscheduled;

const UnscheduledFarmProperty = observer(({ disabled, fieldName, riskIndex }) => {
  const classes = useStyles();
  const { values, setFieldValue, setFieldTouched } = useFormikContext();

  const riskFieldName = `${fieldName}.risks[${riskIndex}]`;
  const { disableForm, setActiveForm } = useActiveFormState(riskFieldName);

  const disableFormFinal = Boolean(disableForm || disabled);

  const address = getIn(values, `${fieldName}.address`);
  const riskCoverages = getIn(values, `${fieldName}.risks[${riskIndex}].coverages`) || {};

  const availableScheduledCoverageTypes =
    appConfig.farm[address.state]?.risks[riskType]?.allowedOptions?.scheduledCoverageTypes ||
    FarmScheduleCoverageTypesByState[address.state] ||
    [];

  const brandDeductibleOptions = appConfig.farm[address.state]?.risks[riskType]?.coverageG?.deductibleOptions;
  const currentDeductible = riskCoverages.deductibleAllOther;

  const blanketDeductibleOptions = useMemo(() => {
    const allowed = brandDeductibleOptions || defaultBlanketDeductibleOptions;

    // Keep the risk's current value selectable-but-disabled when it falls outside the allowed set,
    // otherwise the select has no matching option and renders blank.
    if (currentDeductible && !allowed.some((option) => option.id === currentDeductible)) {
      const currentOption = allOtherDeductibleOptions.find((option) => option.id === currentDeductible);
      if (currentOption) {
        return [...allowed, { ...currentOption, disabled: true }];
      }
    }

    return allowed;
  }, [brandDeductibleOptions, currentDeductible]);

  const handleRemoveRisk = () => {
    const locationRisks = getIn(values, `${fieldName}.risks`);
    const newRisks = locationRisks.filter((_, index) => index !== riskIndex);

    setFieldValue(`${fieldName}.risks`, newRisks);
    setFieldTouched(`${fieldName}.risks`);
    setActiveForm(null);
  };

  return (
    <CollapsibleCard
      content={
        <div className={classes.header}>
          <div className={classes.headerRow}>
            <Label type="infoTitleSmall">FO-6 Unscheduled farm property</Label>
          </div>
        </div>
      }
      contentRight={
        <Field id={fieldName} name={fieldName} type="remove" disabled={disableForm} onClick={handleRemoveRisk} />
      }
      initialOpen={true}
      id={`${fieldName}.FO6Unscheduled`}
    >
      <Grid container spacing={2} className={classes.greenCoverageCard}>
        <Grid item xs={12}>
          <Label type="formSubTitle">Coverages</Label>
          <Label type="whiteSmall" className={classes.coverageSubtitle}>
            This item includes intake of foreign objects.
          </Label>
        </Grid>

        <Field
          xs={4}
          id={`${fieldName}.risks[${riskIndex}].coverages.coverageG`}
          name={`${fieldName}.risks[${riskIndex}].coverages.coverageG`}
          type="numeric"
          mode="dark"
          label="Blanket Limit (G)"
          format={{ thousandSeparator: true, prefix: '$' }}
          disabled={disableFormFinal}
        />

        <Field
          xs={4}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductibleAllOther`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductibleAllOther`}
          type="select"
          options={blanketDeductibleOptions}
          mode="dark"
          label="Blanket deductible"
          disabled={disableFormFinal}
        />

        <BrandFormField
          xs={4}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductibleWindHail`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductibleWindHail`}
          type="value"
          mode="dark"
          label="Wind & Hail deductible"
          riskType={riskType}
          state={address.state}
          coverages={riskCoverages}
        />

        <BrandFormField
          xs={4}
          id={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          name={`${fieldName}.risks[${riskIndex}].coverages.deductibleEarthquake`}
          type="select"
          options={eqDeductibleOptions}
          mode="dark"
          label="Earthquake Deductible"
          disabled={disableFormFinal}
          riskType={riskType}
          state={address.state}
          zip={address?.zip}
        />

        <Field
          xs={6}
          id={`${fieldName}.risks[${riskIndex}].coverages.lossSettlement`}
          name={`${fieldName}.risks[${riskIndex}].coverages.lossSettlement`}
          type="value"
          mode="dark"
          label="Loss Settlement"
          value={
            lookupsJson.farm.lossSettlement.find((o) => o.id === riskCoverages.lossSettlement)?.value ??
            riskCoverages.lossSettlement
          }
        />

        <Field
          xs={6}
          id={`${fieldName}.risks[${riskIndex}].coverages.coinsurance`}
          name={`${fieldName}.risks[${riskIndex}].coverages.coinsurance`}
          type="value"
          mode="dark"
          label="Co Insurance"
          value={riskCoverages.coinsurance != null ? `${riskCoverages.coinsurance * 100}%` : ''}
        />

        <BrandFormField
          xs={6}
          name={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          id={`${fieldName}.risks[${riskIndex}].coverages.hasSnowWeightCoverage`}
          type="checkbox"
          label="Weight of ice, snow, or sleet coverage"
          mode="dark"
          disabled={disableFormFinal}
          className={classes.snowWeightCoverage}
          riskType={riskType}
          state={address.state}
        />

        {/* Farm Income Coverage */}
        <Grid item xs={12}>
          <FarmIncomeCoverageForm
            fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
            disabled={disableFormFinal}
            variant="G"
          />
        </Grid>

        {/* Inland Marine Coverage */}
        {availableScheduledCoverageTypes.includes(FarmScheduleCoverageType.SPP) && (
          <Grid item xs={12}>
            <InlandMarineForm
              fieldName={`${fieldName}.risks[${riskIndex}].coverages.scheduleCoverages`}
              disabled={disableFormFinal}
              riskType={riskType}
              coverages={getIn(values, `${fieldName}.risks[${riskIndex}].coverages`) || {}}
            />
          </Grid>
        )}
      </Grid>
    </CollapsibleCard>
  );
});

UnscheduledFarmProperty.propTypes = {
  disabled: PropTypes.bool,
  fieldName: PropTypes.string.isRequired,
  riskIndex: PropTypes.number.isRequired
};

UnscheduledFarmProperty.defaultProps = {
  disabled: false
};

export default UnscheduledFarmProperty;
